/**
 * Capability checks for local browser Whisper STT.
 */

export function isSecureContext() {
  return typeof window !== "undefined" && window.isSecureContext === true;
}

export function hasGetUserMedia() {
  return (
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices &&
    typeof navigator.mediaDevices.getUserMedia === "function"
  );
}

export function hasMediaRecorder() {
  return typeof MediaRecorder !== "undefined";
}

export function hasCrossOriginIsolation() {
  return (
    typeof window !== "undefined" &&
    typeof window.crossOriginIsolated !== "undefined" &&
    window.crossOriginIsolated === true
  );
}

export function hasSharedArrayBuffer() {
  return typeof SharedArrayBuffer !== "undefined";
}

export function hasWebAssembly() {
  return typeof WebAssembly !== "undefined";
}

export function hasWebSpeechRecognition() {
  return (
    typeof window !== "undefined" &&
    (typeof window.SpeechRecognition === "function" ||
      typeof window.webkitSpeechRecognition === "function")
  );
}

function voiceUserAgent() {
  if (typeof navigator === "undefined") return "";
  return navigator.userAgent || "";
}

/** Phones/tablets: Whisper/WASM hangs (ORT Cache / no WebGPU). Prefer Web Speech or fail. */
export function isMobileVoiceClient() {
  const ua = voiceUserAgent();
  if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua)) return true;
  if (typeof navigator !== "undefined" && navigator.maxTouchPoints > 1 && /Macintosh/i.test(ua)) {
    return true;
  }
  return false;
}

/** iOS Safari / Chrome iOS (WKWebView): SpeechRecognition is flaky. */
export function isIOSVoiceClient() {
  const ua = voiceUserAgent();
  if (/iPhone|iPad|iPod/i.test(ua)) return true;
  if (typeof navigator !== "undefined" && navigator.maxTouchPoints > 1 && /Macintosh/i.test(ua)) {
    return true;
  }
  return false;
}

/**
 * Pick a MediaRecorder MIME type Safari / Chrome will accept.
 * @returns {string|undefined}
 */
export function preferredAudioMimeType() {
  if (!hasMediaRecorder()) return undefined;
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4",
    "audio/aac",
    "audio/ogg;codecs=opus",
  ];
  for (const type of candidates) {
    try {
      if (MediaRecorder.isTypeSupported(type)) return type;
    } catch {
      /* ignore */
    }
  }
  return undefined;
}

/**
 * Rough device memory heuristic (Chrome only). Returns GB or null.
 */
export function deviceMemoryGb() {
  try {
    return navigator.deviceMemory || null;
  } catch {
    return null;
  }
}

/**
 * @typedef {object} CapabilityReport
 * @property {boolean} ok
 * @property {string|null} reason  VoiceState error code when !ok
 * @property {object} details
 */

/**
 * @returns {CapabilityReport}
 */
export function checkBrowserCapability() {
  const details = {
    secureContext: isSecureContext(),
    getUserMedia: hasGetUserMedia(),
    mediaRecorder: hasMediaRecorder(),
    webAssembly: hasWebAssembly(),
    crossOriginIsolated: hasCrossOriginIsolation(),
    sharedArrayBuffer: hasSharedArrayBuffer(),
    mimeType: preferredAudioMimeType() || null,
    deviceMemoryGb: deviceMemoryGb(),
    webSpeech: hasWebSpeechRecognition(),
  };

  if (!details.secureContext) {
    return { ok: false, reason: "UNSUPPORTED_BROWSER", details };
  }
  if (!details.getUserMedia && !details.webSpeech) {
    return { ok: false, reason: "UNSUPPORTED_BROWSER", details };
  }
  if (!details.webSpeech && (!details.mediaRecorder || !details.webAssembly)) {
    return { ok: false, reason: "UNSUPPORTED_BROWSER", details };
  }
  // Low-memory devices may still work with tiny/base quantized; warn but allow.
  if (details.deviceMemoryGb != null && details.deviceMemoryGb < 2) {
    return {
      ok: true,
      reason: null,
      details: { ...details, lowMemoryWarning: true },
    };
  }
  return { ok: true, reason: null, details };
}
